import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Pencil, Trash2, Plus, Send, Car, Mail, Phone, MapPin, Wrench, CalendarDays, Receipt, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, LoadingState, ErrorState, EmptyState, Card, SectionTitle, StatusBadge, ConfirmDialog } from "@/components/common";
import { CustomerForm, DuplicateWarning } from "@/components/pro/CustomerForm";
import { VehicleForm } from "@/components/pro/VehicleForm";
import { StaffHistoryReport } from "@/modules/connect/HistoryReport";
import { MessageCustomerButton } from "@/modules/messaging/Inbox";
import { SimulatedEmailNote } from "@/pages/auth/ForgotPassword";
import { portalStatus } from "@/pages/pro/Customers";
import { Breadcrumbs, SmartButton, SmartButtons } from "@/components/layout/Record";
import { useGarage } from "@/context/GarageContext";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { fullName, fmtNumber, fmtYmd, fmtCents } from "@/lib/format";
import { t } from "@/i18n";

export function VehicleTile({ v, onEdit, onDelete, canEdit }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`vehicle-tile-${v.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <Link to={`/pro/vehicles/${v.id}`} className="inline-block rounded-md border-2 border-slate-800 bg-white px-2 py-0.5 font-mono-plate text-sm font-semibold text-slate-900 hover:bg-yellow-100" data-testid="vehicle-plate">{v.plate}</Link>
          <span className="ml-2 text-xs font-semibold text-slate-500">{v.state}</span>
          <p className="mt-2 font-semibold text-slate-900">{v.make} {v.model} {v.year ? `(${v.year})` : ""}</p>
          <p className="text-xs text-slate-500">{v.odometer != null ? t("vehicles.odoLine", { km: fmtNumber(v.odometer), date: fmtYmd(v.odometer_date) }) : t("vehicles.noOdo")}</p>
          {v.vin && <p className="text-xs text-slate-500">VIN {v.vin}</p>}
        </div>
        {canEdit && (
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" onClick={onEdit} data-testid={`edit-vehicle-${v.id}`} aria-label={t("common.edit")}><Pencil className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" className="text-amber-700" onClick={onDelete} data-testid={`archive-vehicle-${v.id}`} aria-label={t("archive.archive")} title={t("archive.archive")}><Trash2 className="h-4 w-4" /></Button>
          </div>
        )}
      </div>
    </div>
  );
}

function CustomerSmart({ c }) {
  const { garage, isManager, hasFeature } = useGarage();
  const billing = isManager && hasFeature("quotes_and_invoices");
  const appts = useFetch("/appointments");
  const inv = useFetch(billing ? "/invoices" : null, { customer_id: c.id });
  const balance = inv.data?.filter((i) => ["issued", "partially_paid", "overdue"].includes(i.status)).reduce((s, i) => s + (i.balance_cents || 0), 0);
  return (
    <SmartButtons>
      <SmartButton icon={Car} label={t("pro.nav.vehicles")} value={c.vehicles.filter((v) => !v.archived).length} testId="smart-vehicles" />
      <SmartButton icon={Wrench} label={t("jobs.title")} value={t("b13.smart.open")} to={`/pro/jobs?status=all&customer_id=${c.id}`} testId="smart-jobs" />
      <SmartButton icon={CalendarDays} label={t("b13.smart.appointments")} value={appts.data?.filter((a) => a.customer?.id === c.id).length} to="/pro/planning" testId="smart-appointments" />
      {billing && <SmartButton icon={Receipt} label={t("invoices.title")} value={inv.data?.length} to={`/pro/invoices?customer_id=${c.id}`} testId="smart-invoices" />}
      {billing && <SmartButton icon={Wallet} label={t("b13.smart.balance")} value={balance == null ? null : fmtCents(balance, garage.currency)} to={`/pro/invoices?status=unpaid&customer_id=${c.id}`} testId="smart-balance" tone={balance ? "text-amber-700" : ""} />}
    </SmartButtons>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const { readOnly, canManage } = useGarage();
  const { me } = useAuth();
  const { data, error, loading, reload } = useFetch(`/customers/${id}`);
  const [editOpen, setEditOpen] = useState(false);
  const [vehicleDlg, setVehicleDlg] = useState(null);
  const [delVehicle, setDelVehicle] = useState(null);
  const [delCustomer, setDelCustomer] = useState(false);
  const [inviteConfirm, setInviteConfirm] = useState(false);
  const [dups, setDups] = useState(null);
  const [busy, setBusy] = useState(false);

  const act = async (fn, msg, after) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      after?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const c = data;
  const status = portalStatus(c);

  return (
    <div>
      <Breadcrumbs testId="customer-crumbs" items={[[t("b13.nav.customers"), "/pro/customers"], [t("pro.nav.customers"), "/pro/customers"], [fullName(c)]]} />
      <PageHeader
        title={<span>{fullName(c)} {c.archived && <StatusBadge status="archived" testId="customer-archived-badge" />}</span>}
        subtitle={t("customers.since", { date: fmtYmd(c.created_at?.slice(0, 10)) })}
        actions={!readOnly && (
          <>
            <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-customer-button"><Pencil className="mr-2 h-4 w-4" /> {t("common.edit")}</Button>
            {canManage && <Button variant="outline" className={c.archived ? "" : "text-amber-700"} onClick={() => setDelCustomer(true)} data-testid={c.archived ? "restore-customer-button" : "archive-customer-button"}><Trash2 className="mr-2 h-4 w-4" /> {c.archived ? t("archive.restore") : t("archive.archive")}</Button>}
          </>
        )}
      />
      <div className="mb-6"><CustomerSmart c={c} /></div>
      <div className="grid gap-6 lg:grid-cols-[1fr_1.5fr]">
        <div className="space-y-6">
          <Card testId="customer-info-card">
            <SectionTitle>{t("customers.contact")}</SectionTitle>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2" data-testid="customer-email"><Mail className="h-4 w-4 text-slate-400" /> {c.email || "—"}</li>
              <li className="flex items-center gap-2" data-testid="customer-phone"><Phone className="h-4 w-4 text-slate-400" /> {c.phone || "—"}</li>
              <li className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> {c.address || "—"}</li>
            </ul>
            <p className="mt-4 text-xs text-slate-500">{t("customers.commPrefs")}: {["email", "sms", "service_reminders", "marketing"].filter((k) => c.comm_prefs?.[k] ?? k === "service_reminders").map((k) => t(`customers.comm.${k}`)).join(", ") || t("customers.commNone")}</p>
            {c.notes && <p className="mt-4 whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-sm text-slate-700">{c.notes}</p>}
          </Card>
          <Card testId="customer-portal-card">
            <SectionTitle hint={t("customers.portalHint")}>{t("customers.portal")}</SectionTitle>
            <StatusBadge status={status} testId="customer-portal-status" />
            {status !== "active" && !readOnly && (
              <div className="mt-4 space-y-3">
                <Button onClick={() => setInviteConfirm(true)} disabled={!c.email} data-testid="invite-to-portal-button">
                  <Send className="mr-2 h-4 w-4" /> {status === "invited" ? t("customers.resendInvite") : t("customers.inviteToPortal")}
                </Button>
                {!c.email && <p className="text-xs text-slate-500">{t("customers.needEmail")}</p>}
                {status === "invited" && <SimulatedEmailNote simulated={me.demo_mode} />}
              </div>
            )}
          </Card>
        </div>
        <Card testId="customer-vehicles-card">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-900">{t("pro.nav.vehicles")}</h2>
            {!readOnly && <Button size="sm" onClick={() => { setDups(null); setVehicleDlg({}); }} data-testid="add-customer-vehicle-button"><Plus className="mr-1.5 h-4 w-4" /> {t("vehicles.add")}</Button>}
          </div>
          {c.vehicles.length === 0 ? <EmptyState icon={Car} title={t("vehicles.emptyCustomer")} /> : (
            <div className="grid gap-3 sm:grid-cols-2">
              {c.vehicles.filter((v) => !v.archived).map((v) => <div key={v.id} className="space-y-2"><VehicleTile v={v} canEdit={!readOnly} onEdit={() => { setDups(null); setVehicleDlg({ vehicle: v }); }} onDelete={() => setDelVehicle(v)} /><div className="flex flex-wrap gap-2"><StaffHistoryReport vehicle={v} /><MessageCustomerButton vehicleId={v.id} /></div></div>)}
            </div>
          )}
        </Card>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="edit-customer-dialog">
          <DialogHeader><DialogTitle>{t("customers.edit")}</DialogTitle><DialogDescription>{fullName(c)}</DialogDescription></DialogHeader>
          {editOpen && <CustomerForm customer={c} onSaved={() => { setEditOpen(false); toast.success(t("common.saved")); reload(); }} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!vehicleDlg} onOpenChange={(o) => !o && setVehicleDlg(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="vehicle-dialog">
          <DialogHeader><DialogTitle>{vehicleDlg?.vehicle ? t("vehicles.edit") : t("vehicles.add")}</DialogTitle><DialogDescription>{fullName(c)}</DialogDescription></DialogHeader>
          <DuplicateWarning matches={dups} />
          {vehicleDlg && <VehicleForm vehicle={vehicleDlg.vehicle} basePath="/vehicles" customerId={c.id} customers={null} onDuplicate={setDups} onSaved={() => { setVehicleDlg(null); toast.success(t("common.saved")); reload(); }} />}
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!delVehicle} onOpenChange={(o) => !o && setDelVehicle(null)} busy={busy} title={t("archive.vehicleTitle")} description={t("archive.vehicleText", { plate: delVehicle?.plate })} confirmLabel={t("archive.archive")}
        onConfirm={() => act(() => api.post(`/vehicles/${delVehicle.id}/archive`), t("archive.done"), () => { setDelVehicle(null); reload(); })} testId="archive-vehicle-dialog" />
      <ConfirmDialog open={delCustomer} onOpenChange={setDelCustomer} busy={busy} title={c.archived ? t("archive.restoreTitle") : t("archive.customerTitle")} description={c.archived ? t("archive.restoreText") : t("archive.customerText")} confirmLabel={c.archived ? t("archive.restore") : t("archive.archive")}
        onConfirm={() => act(() => api.post(`/customers/${c.id}/${c.archived ? "restore" : "archive"}`), c.archived ? t("archive.restored") : t("archive.done"), () => { setDelCustomer(false); reload(); })} testId="archive-customer-dialog" />
      <ConfirmDialog open={inviteConfirm} onOpenChange={setInviteConfirm} busy={busy} title={t("customers.inviteTitle")} description={t(me.demo_mode ? "customers.inviteTextDemo" : "customers.inviteTextNoProvider", { email: c.email })} confirmLabel={t("customers.createInvite")}
        onConfirm={() => act(() => api.post(`/customers/${c.id}/invite-portal`), t(me.demo_mode ? "invite.createdDemo" : "invite.createdNoProvider"), () => { setInviteConfirm(false); reload(); })} testId="invite-portal-dialog" />
    </div>
  );
}
